import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { useViewport } from '../../../../design-system/hooks/useViewport';
import {
  Button,
  Chip as DesktopChip,
  Facet,
  FacetOption,
  Select,
} from '../../../../design-system/components/desktop';
import { Chip as MobileChip, OptionRow } from '../../../../design-system/components/mobile';
import { routes } from '../../../../routes/routes';
import {
  useCatalogGroupInfo,
  useTcgCategories,
  useTcgRarities,
} from './facetData';
import { CLEARED_FILTERS, CONDITIONS, selectTcgCategory } from './filterModel';

/**
 * The facet panel, shared by `/marketplace` and `/seller/:sellerId`.
 *
 * This is the BODY only. It never renders its own container, because the
 * container is exactly what differs across the 1024px handoff: the page wraps
 * it in a sticky `FilterRail` on desktop and in a filter `Sheet` below that.
 * Rendering both and hiding one is not an option, so the page mounts one.
 *
 * THE MOBILE BRANCH HERE IS THE FALLBACK, NOT THE DESIGN
 * -----------------------------------------------------
 * The library designed a real filter SHEET — drill rows into sub-panels, a
 * searchable set list, a switch for availability, a segmented product type —
 * and that is now `MarketplaceFilterSheet.jsx`, which `/marketplace` mounts.
 * What is left below the handoff in THIS file is the flat stack of every facet
 * expanded at once: correct, reachable, and still what `/seller/:sellerId`
 * renders until it adopts the sheet too. Do not build on it; extend the sheet.
 *
 * WHAT BRANCHES HERE AND WHY
 * --------------------------
 * The facet ROWS branch, because the two tracks genuinely have different
 * controls for the same job: `dsk-facet__option` is a 28px hairline row built
 * for a 256px rail, `ds-option-row` is a 44px tap target. Same state, same
 * callbacks, one tree mounted.
 *
 * WHAT IS DELIBERATELY NOT HERE
 * -----------------------------
 * - A PRICE facet. `marketplace.html` has a price histogram and the library
 *   ships `PriceHistogram` for it, but the app renders no price control today
 *   and the endpoints return no price distribution to draw. Adding an empty
 *   histogram would be a control that lies. The `priceRange` filter is still
 *   carried and still sent, it just has no UI, exactly as before.
 *
 *   AND THERE IS A SECOND REASON NOW, found while building the filter sheet:
 *   `CLEARED_FILTERS.priceRange` is `[0, 1000]` and `Marketplace.jsx` sends it
 *   as `min_price`/`max_price` on EVERY request, yet the grid returns cards at
 *   R21,500. So the ceiling is either dead server-side or it is silently
 *   capping browse at R1,000 — nobody can tell from here. Drawing two price
 *   fields on top of that would either expose a cap the product does not mean
 *   or invent one it does not have. The range needs a decision before it gets
 *   a control; see the note in `filterModel.js`.
 * - A GRADE facet. There is no grade field in this product. Condition is real
 *   and has its own facet.
 * - The `category` and `type` filters. Both are in the filter object and both
 *   are sent to the API, and neither has ever had a control. Left as they were.
 */

const MarketplaceFilters = ({ filters, onFilterChange, onClearAll, showClearAll }) => {
  const { isDesktop } = useViewport();
  const [localFilters, setLocalFilters] = useState(filters);

  // Keep the local copy in sync when the parent's filters change after mount
  // (e.g. the set filter is applied from the URL slightly later). Without this,
  // localFilters goes stale and the next updateFilter() spreads the stale copy,
  // silently dropping filters like catalog_group_id that the parent had set.
  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  /* The three facet fetches live in `facetData.js`, shared with the filter
     sheet. Same endpoints, same cancellation, one copy. */
  const { tcgCategories, loadingCategories } = useTcgCategories();
  const { rarities, loadingRarities } = useTcgRarities(localFilters.tcg_category_id);
  const { catalogGroupInfo, loadingCatalogGroup } = useCatalogGroupInfo(
    localFilters.catalog_group_id,
    localFilters.tcg_category_id,
    tcgCategories,
  );

  /** Generic handler to update filters */
  const updateFilter = (key, value) => {
    const newFilters = { ...localFilters, [key]: value };
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  /** Changing the TCG drops the rarity AND the set, both of which belong to one
   *  game — see `selectTcgCategory` in filterModel.js for why. */
  const handleTcgCategoryChange = (value) => {
    const newFilters = selectTcgCategory(localFilters, value);
    setLocalFilters(newFilters);
    onFilterChange(newFilters);
  };

  /** Toggle multi-select checkboxes (for rarity & condition) */
  const toggleMultiSelect = (key, option) => {
    let currentValues = Array.isArray(localFilters[key]) ? [...localFilters[key]] : [];

    if (currentValues.includes(option)) {
      currentValues = currentValues.filter((item) => item !== option);
    } else {
      currentValues.push(option);
    }

    updateFilter(key, currentValues);
  };

  /* The panel always resets its own local copy; what "clear" means BEYOND the
     facets is the page's business, because the search term and the page number
     do not live here. A page that hands down `onClearAll` gets the full reset;
     one that does not keeps the facets-only behaviour this panel always had. */
  const clearAllFilters = () => {
    setLocalFilters(CLEARED_FILTERS);
    if (onClearAll) onClearAll();
    else onFilterChange(CLEARED_FILTERS);
  };

  const selectedRarities = Array.isArray(localFilters.rarity) ? localFilters.rarity : [];
  const selectedConditions = Array.isArray(localFilters.condition) ? localFilters.condition : [];

  /* One row, two tracks. Everything above this line is shared state; below it
     is the only place the DOM diverges. */
  const checkRow = (key, label, checked, onChange) =>
    isDesktop ? (
      <FacetOption key={key} label={label} checked={checked} onChange={onChange} />
    ) : (
      <OptionRow
        key={key}
        control="checkbox"
        label={label}
        inputProps={{ checked, onChange: (event) => onChange(event.target.checked, event) }}
      />
    );

  const radioRow = (key, name, label, checked, onChange) =>
    isDesktop ? (
      <label key={key} className="dsk-facet__option">
        <input
          type="radio"
          name={name}
          className="dsk-check"
          checked={checked}
          onChange={onChange}
          /* Re-clicking the chosen radio clears it, which is how the app has
             always let a buyer step back out of "Cards only". A radio has no
             native way to do that, so the click does it explicitly. */
          onClick={checked ? onChange : undefined}
        />
        <span>{label}</span>
      </label>
    ) : (
      <OptionRow
        key={key}
        control="radio"
        label={label}
        inputProps={{ name, checked, onChange, onClick: checked ? onChange : undefined }}
      />
    );

  const hint = (text) => (
    <p className={isDesktop ? 'dsk-hint' : 'ds-sheet__subtitle'}>{text}</p>
  );

  /* The applied set reads as one chip on both tracks, and the chip IS the
     remove control on both, so there is no hover-only affordance to make
     keyboard reachable. Only the class prefix differs. */
  const Chip = isDesktop ? DesktopChip : MobileChip;
  const chipProps = isDesktop ? { active: true } : { variant: 'active' };

  const section = (title, count, children) =>
    isDesktop ? (
      <Facet title={title} count={count}>
        {children}
      </Facet>
    ) : (
      // The mobile track has no facet block. `.dsk-facet` is desktop only and
      // `mobile/*.css` never promoted an equivalent, so the scaffold is
      // `.dsa-facet` in app-layer.css: a stack, a gap, and a label at exactly
      // `.dsk-facet__head`'s two token values. No colour, no radius, nothing
      // with a visual identity of its own. Every row inside is a real
      // `.ds-option-row`, and the count pill is the real `.ds-applied-chip`.
      <section className="dsa-facet">
        <h3 className="dsa-facet__title">
          {title}
          {count ? <span className="ds-applied-chip">{count}</span> : null}
        </h3>
        {children}
      </section>
    );

  return (
    <>
      {section(
        'Availability',
        undefined,
        checkRow('avail', 'Available to Buy', Boolean(localFilters.available_to_buy), () =>
          updateFilter('available_to_buy', !localFilters.available_to_buy),
        ),
      )}

      {section(
        'TCG',
        localFilters.tcg_category_id ? 1 : undefined,
        loadingCategories ? (
          hint('Loading')
        ) : isDesktop ? (
          <Select
            options={[
              { value: '', label: 'All TCGs' },
              ...tcgCategories.map((tcg) => ({
                value: String(tcg.id),
                label: tcg.name || tcg.title,
              })),
            ]}
            value={localFilters.tcg_category_id ? String(localFilters.tcg_category_id) : ''}
            onChange={handleTcgCategoryChange}
            placeholder="All TCGs"
            label="TCG"
          />
        ) : (
          <>
            {radioRow('tcg-all', 'tcgCategory', 'All TCGs', !localFilters.tcg_category_id, () =>
              handleTcgCategoryChange(''),
            )}
            {tcgCategories.map((tcg) =>
              radioRow(
                `tcg-${tcg.id}`,
                'tcgCategory',
                tcg.name || tcg.title,
                String(localFilters.tcg_category_id) === String(tcg.id),
                () => handleTcgCategoryChange(tcg.id),
              ),
            )}
          </>
        ),
      )}

      {section(
        'Set',
        localFilters.catalog_group_id ? 1 : undefined,
        localFilters.catalog_group_id ? (
          <Chip
            {...chipProps}
            onClick={() => updateFilter('catalog_group_id', '')}
            aria-label={
              catalogGroupInfo
                ? `Remove set filter: ${catalogGroupInfo.setName}`
                : 'Remove set filter'
            }
          >
            {loadingCatalogGroup
              ? 'Loading'
              : catalogGroupInfo
                ? `${catalogGroupInfo.tcgName} ${catalogGroupInfo.setName}`
                : localFilters.catalog_group_id}
          </Chip>
        ) : isDesktop ? (
          <Button as={Link} to={routes.SETS} variant="ghost" size="sm">
            Browse by set
          </Button>
        ) : (
          // `Cta` is always a <button>, and this navigates, so it has to be a
          // real link. The classes are the library's own `.ds-cta` ghost, not
          // a page-local button: a link that acts as a CTA still looks like one.
          <Link className="ds-cta ds-cta--ghost ds-cta--sm" to={routes.SETS}>
            Browse by set
          </Link>
        ),
      )}

      {section(
        'Rarity',
        selectedRarities.length || undefined,
        !localFilters.tcg_category_id
          ? hint('Select a TCG to filter by rarity.')
          : loadingRarities
            ? hint('Loading')
            : rarities.length === 0
              ? hint('No rarities available for this TCG.')
              : rarities.map((rarity) =>
                  checkRow(`rarity-${rarity}`, rarity, selectedRarities.includes(rarity), () =>
                    toggleMultiSelect('rarity', rarity),
                  ),
                ),
      )}

      {section(
        'Condition',
        selectedConditions.length || undefined,
        CONDITIONS.map((condition) =>
          checkRow(
            `condition-${condition}`,
            condition,
            selectedConditions.includes(condition),
            () => toggleMultiSelect('condition', condition),
          ),
        ),
      )}

      {section(
        'Product type',
        localFilters.productType ? 1 : undefined,
        <>
          {radioRow('pt-card', 'productType', 'Cards only', localFilters.productType === 'card', () =>
            updateFilter('productType', localFilters.productType === 'card' ? '' : 'card'),
          )}
          {radioRow(
            'pt-sealed',
            'productType',
            'Sealed only',
            localFilters.productType === 'sealed',
            () => updateFilter('productType', localFilters.productType === 'sealed' ? '' : 'sealed'),
          )}
        </>,
      )}

      {/* The rail carries its own clear, exactly where marketplace.html puts
          it. Below the handoff the sheet footer owns it instead, so the panel
          would otherwise show two. */}
      {(showClearAll ?? isDesktop) && (
        <Button variant="ghost" onClick={clearAllFilters}>
          Clear all filters
        </Button>
      )}
    </>
  );
};

export default MarketplaceFilters;
